import Box from '@ahamove/web-components/lib/Box';
import Button from '@ahamove/web-components/lib/Button';
import List from '@ahamove/web-components/lib/List';
import Paper from '@ahamove/web-components/lib/Paper';
import Stack from '@ahamove/web-components/lib/Stack';
import Typography from '@ahamove/web-components/lib/Typography';
import Image from 'next/legacy/image';
// import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { useEVStore } from '@/global-stores/useEVStore';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { useNewBooking } from '../NewBookingProvider';
import EVItem from './EVItem';
import EVItemLoading from './EVItemLoading';
import EVSelectionHeader from './EVSelectionHeader';

type Props = {
  availableCities: AvailableCity[];
};

export default function EVSelection({ availableCities }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const {
    availableEVs,
    loading,
    activeStep,
    goToCheckoutPage,
    estimateFeeError,
  } = useNewBooking();
  const { selectedEVId, setSelectedEVId } = useEVStore();
  // const { pathname, replace, query } = useRouter();

  const { serviceModels } = useNewBooking();

  React.useEffect(() => {
    if (availableEVs) {
      const newId = availableEVs[0] ? availableEVs[0].service_id : null;
      if (selectedEVId) {
        const existVehicle = availableEVs.find(
          (ev) => ev.service_id === selectedEVId
        );
        if (!existVehicle) {
          setSelectedEVId(newId);
          // const { webinapp } = query;
          // const existWebInApp = webinapp ? { webinapp } : {};
          // replace(
          //   {
          //     pathname,
          //     query: newId
          //       ? { service_id: newId, ...existWebInApp }
          //       : { ...existWebInApp },
          //   },
          //   undefined,
          //   {
          //     shallow: true,
          //   }
          // );
        }
      } else {
        setSelectedEVId(newId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEVs]);

  if (activeStep === 0) return <React.Fragment />;

  return (
    <Box
      sx={{
        pt: isMobile ? 23 : 13,
        pb: 7.5,
        minHeight: '60vh',
        bgcolor: isMobile ? 'common.white' : 'grey.50',
      }}
    >
      <main className="desktop:px-8 max-w-8xl mx-auto px-4">
        {availableEVs ? (
          <Paper
            elevation={0}
            sx={
              isMobile
                ? { py: 7.5 }
                : {
                    px: 5,
                    py: 6,

                    borderRadius: 8,
                  }
            }
          >
            <Stack direction="column" spacing={isMobile ? 3.5 : 5}>
              <EVSelectionHeader availableCities={availableCities} />
              <List className="space-y-6">
                {availableEVs.map((vehicle) => (
                  <EVItem key={vehicle._id} data={vehicle} />
                ))}
              </List>
              <Stack
                direction="row"
                justifyContent={isMobile ? 'center' : 'flex-end'}
                sx={
                  isMobile
                    ? {
                        width: '100%',
                        p: isMobile ? 0 : 2,
                        bgcolor: 'common.white',
                      }
                    : {}
                }
              >
                <Button
                  fullWidth={isMobile}
                  rounded
                  sx={(theme) => ({
                    ...theme.typography.h3,
                    minHeight: 56,
                    minWidth: 200,
                  })}
                  onClick={goToCheckoutPage}
                  data-test-id="selectServiceBtn"
                >
                  {t('booking:continue')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : loading ? (
          <Paper
            elevation={0}
            sx={
              isMobile
                ? { py: 7.5 }
                : {
                    px: 5,
                    py: 6,

                    borderRadius: 8,
                  }
            }
          >
            <Stack direction="column" spacing={isMobile ? 3.5 : 5}>
              <EVSelectionHeader availableCities={availableCities} />
              <List className="space-y-6">
                {Array.from(Array(3).keys()).map((item) => (
                  <EVItemLoading key={item} />
                ))}
              </List>
            </Stack>
          </Paper>
        ) : serviceModels && serviceModels.length > 0 ? (
          <Stack
            direction="column"
            spacing={isMobile ? 2 : 2.75}
            alignItems="center"
            mx="auto"
            px={isMobile ? 0 : 5}
            py={8}
          >
            <Box
              sx={{
                width: 156,
                height: 177,
                mx: 'auto',
                position: 'relative',
              }}
            >
              <Image
                src={'/static/images/empty-search.png'}
                alt="empty-search"
                layout="fill"
                objectFit="contain"
              />
            </Box>
            <Typography
              variant={isMobile ? 'h4' : 'h1'}
              component="h2"
              color="grey.800"
              textAlign="center"
              sx={(theme) => ({
                mt: `${theme.spacing(isMobile ? 3 : 5.5)} !important`,
              })}
            >
              {t('booking:ev.no_available_ev')}
            </Typography>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              color="grey.600"
              textAlign="center"
            >
              {t('booking:ev.no_available_ev_desc')}
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction="column"
            spacing={isMobile ? 2 : 2.75}
            alignItems="center"
            mx="auto"
            px={isMobile ? 0 : 5}
            py={8}
          >
            <Box
              sx={{
                width: 332,
                height: 177,
                mx: 'auto',
                position: 'relative',
              }}
            >
              <Image
                src={'/static/images/empty-location.png'}
                alt="empty-location"
                layout="fill"
                objectFit="contain"
              />
            </Box>
            <Typography
              variant={isMobile ? 'h4' : 'h1'}
              component="h2"
              color="grey.800"
              textAlign="center"
              sx={(theme) => ({
                mt: `${theme.spacing(isMobile ? 3 : 5.5)} !important`,
              })}
            >
              {estimateFeeError?.description ??
                t('booking:location.not_arrived_here')}
            </Typography>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              color="grey.600"
              textAlign="center"
            >
              {estimateFeeError?.title ??
                t('booking:location.not_arrived_here_desc')}
            </Typography>
          </Stack>
        )}
      </main>
    </Box>
  );
}
